import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import { useIsTabletOrMobile } from "@/hooks/use-tablet-mobile";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAnimatedQuotes } from "@/hooks/useAnimatedQuotes";
const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const isTabletOrMobile = useIsTabletOrMobile();
  const isMobile = useIsMobile();
  const {
    currentQuote,
    isVisible
  } = useAnimatedQuotes();
  const {
    user,
    signInWithEmail,
    signInWithGoogle
  } = useAuth();

  // Redirect if already logged in
  if (user) {
    return <Navigate to="/" replace />;
  }
  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    const {
      error
    } = await signInWithEmail(email, password);
    if (error) {
      toast({
        title: "Login failed",
        description: error.message,
        variant: "destructive"
      });
    } else {
      toast({
        title: "Login successful",
        description: "Welcome back!"
      });
    }
    setIsLoading(false);
  };
  const handleGoogleLogin = async () => {
    const {
      error
    } = await signInWithGoogle();
    if (error) {
      toast({
        title: "Google login failed",
        description: error.message,
        variant: "destructive"
      });
    }
  };
  const FooterContent = () => (
    <div className="w-full">
      <div className="flex justify-between items-center text-sm text-gray-400 mb-4">
        <div className="flex space-x-2">
          <Link to="/terms" className="hover:text-gray-600 transition-colors">
            Terms of Use
          </Link>
          <Link to="/privacy" className="hover:text-gray-600 transition-colors">
            Privacy Policy
          </Link>
        </div>
        <p className="text-xs">© 2025 Studibudi</p>
      </div>
    </div>
  );
  if (isTabletOrMobile) {
    return <div className={`min-h-screen flex flex-col bg-app-background`}>
      {/* Top Navigation */}
      <nav className="w-full px-4 h-[58px] flex items-center">
        <div className="flex justify-start">
          <img src="/Logo/sblogo.png" alt="Studibudi Logo" className="h-6 w-auto" />
        </div>
      </nav>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div style={!isMobile ? {
            backdropFilter: 'blur(40px)'
          } : {
            backdropFilter: 'blur(40px)'
          }} className={`backdrop-blur-3xl rounded-2xl`}>
            {/* Login Form */}
            <div className="space-y-6">
              <div className="text-center">
                <h2 className="text-xl font-heading font-semibold text-gray-900 mb-6">Welcome back, learner!</h2>
              </div>

              <form onSubmit={handleEmailLogin} className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium text-gray-700">Email</Label>
                  <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="john@example.com" className="w-full h-10 px-4 bg-gray-100 border border-gray-300 rounded-2xl text-gray-800 placeholder:text-gray-400 placeholder:font-light focus:outline-none focus:ring-0" required disabled={isLoading} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-medium text-gray-700">Password</Label>
                  <Input id="password" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter your password" className="w-full h-10 px-4 bg-gray-100 border border-gray-300 rounded-2xl text-gray-800 placeholder:text-gray-400 placeholder:font-light focus:outline-none focus:ring-0" required disabled={isLoading} />
                  <div className="text-right">
                    <Link to="/forgot-password" className="text-sm text-blue-600 hover:text-blue-700 transition-colors">
                      Forgot Password?
                    </Link>
                  </div>
                </div>

                <div className="pt-3">
                  <Button type="submit" className="w-full h-12 bg-black hover:bg-gray-800 text-white font-medium rounded-2xl border-0 shadow-none" disabled={isLoading}>
                    {isLoading ? <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Logging in...
                    </> : "Log in"}
                  </Button>
                </div>
              </form>

              {/* Divider */}
              <div className="text-center text-gray-400 text-sm my-4">or</div>

              {/* Social Login */}
              <div className="space-y-3 mb-6">
                <Button variant="outline" className="w-full h-12 border border-gray-200 hover:bg-gray-50 bg-white rounded-2xl shadow-none" onClick={handleGoogleLogin} disabled={isLoading}>
                  <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                  Log in with Google
                </Button>
              </div>

              {/* Links */}
              <div className="flex justify-between text-sm mt-6 mb-6">
                <span className="text-gray-600 mr-1">Don't have an account?</span>
                <Link to="/signup" className="text-blue-600 hover:text-blue-700 transition-colors">
                  Sign up
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer at bottom */}
      <footer className="w-full px-4 pb-4">
        <FooterContent />
      </footer>
    </div>;
  }

  // Desktop layout with equal space distribution
  return <div className="min-h-screen flex flex-col bg-app-background">
    {/* Top Navigation */}
    <nav className="w-full px-4 h-[58px] flex items-center">
      <div className="flex justify-start">
        <img src="/Logo/sblogo.png" alt="Studibudi Logo" className="h-6 w-auto" />
      </div>
    </nav>

    {/* Main Content */}
    <div className="flex-1 flex items-center justify-center px-[80px]">
      {/* Login Form */}
      <div className="flex-1 flex items-center justify-center">
        <div style={{
          backdropFilter: 'blur(40px)'
        }} className="w-full backdrop-blur-3xl max-w-md rounded-2xl">
          {/* Login Form */}
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-xl font-heading font-semibold text-gray-900 mb-6">Welcome back, learner!</h2>
            </div>

            <form onSubmit={handleEmailLogin} className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-gray-700">Email</Label>
                <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="john@example.com" className="w-full h-10 px-4 bg-gray-100 border border-gray-300 rounded-2xl text-gray-800 placeholder:text-gray-400 placeholder:font-light focus:outline-none focus:ring-0" required disabled={isLoading} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-gray-700">Password</Label>
                <Input id="password" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter your password" className="w-full h-10 px-4 bg-gray-100 border border-gray-300 rounded-2xl text-gray-800 placeholder:text-gray-400 placeholder:font-light focus:outline-none focus:ring-0" required disabled={isLoading} />
                <div className="text-right">
                  <Link to="/forgot-password" className="text-sm text-blue-600 hover:text-blue-700 transition-colors">
                    Forgot Password?
                  </Link>
                </div>
              </div>

              <div className="pt-3">
                <Button type="submit" className="w-full h-12 bg-black hover:bg-gray-800 text-white font-medium rounded-2xl border-0 shadow-none" disabled={isLoading}>
                  {isLoading ? <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Logging in...
                  </> : "Log in"}
                </Button>
              </div>
            </form>

            {/* Divider */}
            <div className="text-center text-gray-400 text-sm my-4">or</div>

            {/* Social Login */}
            <div className="space-y-3 mb-6">
              <Button variant="outline" className="w-full h-12 border border-gray-200 hover:bg-gray-50 bg-white rounded-2xl shadow-none" onClick={handleGoogleLogin} disabled={isLoading}>
                <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                Log in with Google
              </Button>
            </div>

            {/* Links */}
            <div className="flex justify-between text-sm mt-6 mb-3">
              <span className="text-gray-600 mr-1">Don't have an account?</span>
              <Link to="/signup" className="text-blue-600 hover:text-blue-700 transition-colors">
                Sign up
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Footer at bottom */}
    <footer className="w-full px-4 pb-4">
      <FooterContent />
    </footer>
  </div>;
};
export default LoginPage;